import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Pagination, Collapse, Table, Tag, Input, Select, Space, message, Empty } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import axios from "axios";
import PropTypes from "prop-types";
import { formatDateTime } from "../../helpers/date";
import { formatAmount } from "../../helpers/billingStatement";
import { BILLING_CHARGE_TYPES, BILLING_CHARGE_STATUSES, BILLING_CHARGE_STATUS_COLORS } from "../../constants/billingCharge";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const BillingChargeList = ({ initialStatementId }) => {
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ statementId: initialStatementId });
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(initialStatementId ? ["filters"] : []);

  // BillingPage keeps this component mounted across tab switches (Antd Tabs doesn't unmount
  // inactive panes by default), so the initial useState above only seeds the very first open --
  // this effect re-applies the filter (and re-opens the panel) each time a new "Charges" link is
  // clicked from a different Invoices row. Mirrors BillingTransactionList's initialStatementId.
  useEffect(() => {
    if (!initialStatementId) return;
    setFilters((f) => ({ ...f, statementId: initialStatementId }));
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setFiltersOpen(["filters"]);
  }, [initialStatementId]);

  const buildFilterParams = useCallback(
    () => ({
      ...(filters.userEmail && { search: filters.userEmail, searchField: "userEmail" }),
      ...(filters.status && { status: filters.status }),
      ...(filters.billingPeriod && { billingPeriod: filters.billingPeriod }),
      ...(filters.chargeType && { chargeType: filters.chargeType }),
      ...(filters.consignNo && { consignNo: filters.consignNo }),
      ...(filters.statementId && { statementId: filters.statementId }),
    }),
    [filters],
  );

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        page: pagination.currentPage,
        limit: pagination.limit,
      };
      const res = await axios.get("/api/billing-charges", { params });
      setItems(res.data.data);
      setPagination((prev) => ({ ...prev, totalSize: res.data.pagination?.totalSize }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, buildFilterParams]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const handleFilterChange = (updater) => {
    setFilters(updater);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const [filterResetKey, setFilterResetKey] = useState(0);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const clearAllFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setFilterResetKey((k) => k + 1);
  };

  const userEmailDebounce = useRef(null);
  useEffect(() => () => clearTimeout(userEmailDebounce.current), []);
  const handleUserEmailChange = (e) => {
    const value = e.target.value.trim() || undefined;
    clearTimeout(userEmailDebounce.current);
    userEmailDebounce.current = setTimeout(() => {
      handleFilterChange((f) => ({ ...f, userEmail: value }));
    }, 400);
  };

  const consignNoDebounce = useRef(null);
  useEffect(() => () => clearTimeout(consignNoDebounce.current), []);
  const handleConsignNoChange = (e) => {
    const value = e.target.value.trim() || undefined;
    clearTimeout(consignNoDebounce.current);
    consignNoDebounce.current = setTimeout(() => {
      handleFilterChange((f) => ({ ...f, consignNo: value }));
    }, 400);
  };

  const columns = [
    {
      title: "Consign No.",
      key: "consignNo",
      width: 160,
      render: (_, r) => `${r.consignNo}${r.serviceProvider ? ` (${r.serviceProvider})` : ""}`,
    },
    {
      title: "Merchant",
      key: "merchant",
      width: 200,
      ellipsis: true,
      render: (_, r) => r.merchant?.name || r.merchant?.objectId || "-",
    },
    {
      title: "User Email",
      dataIndex: "userEmail",
      key: "userEmail",
      width: 220,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Charge Type",
      dataIndex: "chargeType",
      key: "chargeType",
      width: 220,
    },
    {
      title: "Amount",
      key: "amount",
      width: 130,
      align: "right",
      render: (_, r) => <span style={{ fontWeight: 500 }}>{formatAmount(r.amount, r.currency)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => <Tag color={BILLING_CHARGE_STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Billed In",
      key: "billedIn",
      width: 200,
      // A charge is billed into at most one of these three -- never more than one (see
      // BillingChargePlaceholder on the API). Falls back to "-" while still UNBILLED/RESERVED.
      render: (_, r) => {
        if (r.billingStatement) {
          return (
            <span>
              <Tag color="geekblue">Statement</Tag> {r.billingStatement.statementNumber || r.billingStatement.objectId}
            </span>
          );
        }
        if (r.creditNote) {
          return (
            <span>
              <Tag color="green">CN</Tag> {r.creditNote.documentNumber || r.creditNote.objectId}
            </span>
          );
        }
        if (r.debitNote) {
          return (
            <span>
              <Tag color="volcano">DN</Tag> {r.debitNote.documentNumber || r.debitNote.objectId}
            </span>
          );
        }
        return "-";
      },
    },
    {
      title: "Billing Period",
      dataIndex: "billingPeriod",
      key: "billingPeriod",
      width: 120,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 160,
    },
    {
      title: "Recognition Date",
      dataIndex: "recognitionDate",
      key: "recognitionDate",
      width: 160,
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Billed At",
      dataIndex: "billedAt",
      key: "billedAt",
      width: 160,
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
  ];

  return (
    <div>
      <Collapse
        activeKey={filtersOpen}
        onChange={setFiltersOpen}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      >
        <Collapse.Panel
          key="filters"
          header={
            <Space>
              <span>Filters</span>
              {activeFilterCount > 0 && <Tag color="blue">{activeFilterCount} active</Tag>}
            </Space>
          }
          extra={
            activeFilterCount > 0 && (
              <a onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}>Clear all</a>
            )
          }
        >
          <Row gutter={[16, 16]} key={filterResetKey}>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Merchant Email</span>
                <Input
                  allowClear
                  defaultValue={filters.userEmail}
                  placeholder="Search by merchant account email"
                  onChange={handleUserEmailChange}
                />
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Consign No.</span>
                <Input
                  allowClear
                  defaultValue={filters.consignNo}
                  placeholder="Search by consignment note"
                  onChange={handleConsignNoChange}
                />
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Charge Type</span>
                <Select
                  allowClear
                  placeholder="ALL"
                  style={{ width: "100%" }}
                  onChange={(v) => handleFilterChange((f) => ({ ...f, chargeType: v }))}
                >
                  {BILLING_CHARGE_TYPES.map((type) => (
                    <Select.Option key={type} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Status</span>
                <Select
                  allowClear
                  placeholder="ALL"
                  style={{ width: "100%" }}
                  onChange={(v) => handleFilterChange((f) => ({ ...f, status: v }))}
                >
                  {BILLING_CHARGE_STATUSES.map((status) => (
                    <Select.Option key={status} value={status}>
                      {status}
                    </Select.Option>
                  ))}
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Billing Period</span>
                <Input
                  allowClear
                  placeholder="YYYY-MM"
                  onChange={(e) =>
                    handleFilterChange((f) => ({ ...f, billingPeriod: e.target.value.trim() || undefined }))
                  }
                />
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Statement Id</span>
                <Input
                  allowClear
                  placeholder="BillingStatement objectId"
                  value={filters.statementId || ""}
                  onChange={(e) =>
                    handleFilterChange((f) => ({ ...f, statementId: e.target.value.trim() || undefined }))
                  }
                />
              </Space>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={activeFilterCount > 0 ? "No charges match these filters" : "No billing charges yet"}
              style={{ padding: "24px 0" }}
            >
              {activeFilterCount > 0 && <a onClick={clearAllFilters}>Clear filters</a>}
            </Empty>
          ),
        }}
        style={{ marginTop: 16 }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>
    </div>
  );
};

BillingChargeList.propTypes = {
  initialStatementId: PropTypes.string,
};

export default BillingChargeList;
